import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import { getQueryParamsFromHash } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import parsePermittedQueryParams from '@functions/utils/parsePermittedQueryParams'

import CreateOrEditArtworkTemplateGroupModal from '@modals/CreateOrEditArtworkTemplateGroupModal'

import type { ArtworkTemplateGroupModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditArtworkTemplateGroupModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditArtworkTemplateGroupModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditArtworkTemplateGroupModal: {
      closeModal: () => setState({ showCreateOrEditArtworkTemplateGroupModal: false }),
      createArtworkTemplateGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateArtworkTemplateGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditArtworkTemplateGroupModal } = state

  useLocation()

  const { isArchived } = parsePermittedQueryParams(getQueryParamsFromHash(), ['isArchived'])
  const initDefaultFilters = isArchived ? { isArchived } : {}

  const filtersPayload = useFilters({
    defaultFilters: initDefaultFilters,
    persistFilters: true,
  })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditArtworkTemplateGroupModal: (payload: { artworkTemplateGroup: ArtworkTemplateGroupModel }) => {
        setModalData('CreateOrEditArtworkTemplateGroupModal', payload)
        setState({ showCreateOrEditArtworkTemplateGroupModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditArtworkTemplateGroupModal
          callbacks={callbacks('CreateOrEditArtworkTemplateGroupModal', setState)}
          showModal={showCreateOrEditArtworkTemplateGroupModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
